<template>
  <el-dialog v-loading="loading" :title="title" :visible.sync="dialogVisible" top="8vh" width="600px"
    element-loading-text="加载中" @close="handleClose">
    <el-form v-if="dialogVisible" ref="formRef" :model="form" :rules="rules" label-width="120px">
      <!-- 退款方式 -->
      <el-form-item label="退款方式" prop="refund_type">
        <el-radio-group v-model="form.refund_type" @change="onRefundTypeChange">
          <el-radio :label="0">全额退款</el-radio>
          <el-radio :label="1">部分退款</el-radio>
        </el-radio-group>
      </el-form-item>

      <!-- 退款商品列表 -->
      <el-form-item v-if="isGoodsOrder && form.goods_order_apply && form.goods_order_apply.length" label="退款商品">
        <div v-for="(item, index) in form.goods_order_apply" :key="index" class="refund-goods-item">
          <img :src="item.goods.pic" alt="" class="goods-img" />
          <div class="goods-info">
            <span>规格：{{ item.goods.sku }}</span>
            <span>数量：{{ item.refund_num }}</span>
            <span>价格：{{ item.apply_amount }}</span>
          </div>
        </div>
      </el-form-item>

      <!-- 退款金额（部分退款时显示） -->
      <el-form-item v-if="isPartialRefund" label="退款金额" prop="refund_amount">
        <el-input v-model="form.refund_amount" type="number" :placeholder="`最大可退 ${form.total_price} 元`"
          @input="validateRefundAmount" />
        <span class="form-tip">最大可退：{{ form.total_price }} 元</span>
      </el-form-item>

      <!-- 退款积分（部分退款时显示） -->
      <el-form-item v-if="isPartialRefund && maxRefundPoints > 0" label="退款积分" prop="refund_points">
        <el-input v-model="form.refund_points" type="number" :placeholder="`最大可退 ${maxRefundPoints} 积分`"
          @input="validateRefundPoints" />
        <span class="form-tip">最大可退：{{ maxRefundPoints }} 积分</span>
      </el-form-item>
      <el-form-item label="订单金额">
        <el-input :value="form.total_price || '0.00'" disabled />
      </el-form-item>
      <!-- 可退余额 -->
      <el-form-item label="可退余额">
        <el-input :value="form.user_balance || '0.00'" disabled />
      </el-form-item>

      <!-- 可退现金 -->
      <el-form-item label="可退现金">
        <el-input :value="form.price || '0.00'" disabled />
      </el-form-item>

      <!-- 退款原因 -->
      <el-form-item label="退款原因">
        <el-input v-model="form.remark" disabled type="textarea" :rows="2" />
      </el-form-item>
      <el-form-item>
        <div style="color: red;">点击驳回或者同意按钮后可填写备注</div>
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="handleClose">返回</el-button>
      <el-button type="warning" :loading="loading" @click="handleRefundReject">退款驳回</el-button>
      <el-button type="primary" :loading="loading" @click="handleRefundAgree">退款同意</el-button>
    </template>
  </el-dialog>
</template>

<script>
import {
  goods_refund,
  refuse_refund,
  buy_refund,
  order_apply_refund_detail,
  buy_order_apply_refund_detail
} from '@/api/xgcApi'

// 订单类型常量
const ORDER_TYPE = {
  GOODS: 5, // 商品订单
  BUY: 3, // 代买
  PAY: 4 // 代付
}

export default {
  name: 'RefundDialog',
  data() {
    return {
      dialogVisible: false,
      loading: false,
      form: {
        id: '',
        refund_type: 0, // 0-全额 1-部分
        refund_amount: null,
        refund_points: null,
        user_balance: '0.00',
        price: '0.00',
        remark: '',
        goods_order_apply: [],
        // 原始数据
        _order_price: 0,
        _refunded_price: 0,
        _deduction_points: 0,
        _refunded_points: 0
      },
      orderType: ORDER_TYPE.GOODS,
      rules: {
        refund_amount: [
          { validator: this.validateAmount, trigger: 'blur' }
        ],
        refund_points: [
          { validator: this.validatePoints, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    title() {
      const map = {
        [ORDER_TYPE.GOODS]: '商品退款',
        [ORDER_TYPE.BUY]: '代买退款',
        [ORDER_TYPE.PAY]: '代付退款'
      }
      return map[this.orderType] || '申请退款'
    },
    isGoodsOrder() {
      return this.orderType === ORDER_TYPE.GOODS
    },
    isPartialRefund() {
      return this.form.refund_type === 1
    },
    maxRefundAmount() {
      const max = Number(this.form.total_price)
      return max > 0 ? max.toFixed(2) : '0.00'
    },
    maxRefundPoints() {
      const max = Number(this.form._deduction_points) - Number(this.form._refunded_points)
      return max > 0 ? max : 0
    }
  },
  methods: {
    // 打开弹窗（父组件调用）
    open(row, type) {
      this.orderType = type || ORDER_TYPE.GOODS
      this.form.id = row?.id || ''
      this.dialogVisible = true
      this.loadDetail()
    },

    // 获取退款详情
    async loadDetail() {
      if (!this.form.id) return

      this.loading = true
      try {
        const api = this.isGoodsOrder ? order_apply_refund_detail : buy_order_apply_refund_detail
        const res = await api({ order_id: this.form.id })
        const data = res.data || {}

        this.form = {
          ...this.form,
          ...data,
          _order_price: data.price || 0,
          _refunded_price: data.refund_price || 0,
          _deduction_points: data.deduction_points || 0,
          _refunded_points: data.refund_points || 0,
          refund_amount: null,
          refund_points: null,
          refund_type: 0 // 默认全额
        }

        // 如果是商品订单且有商品列表，保留商品数据
        if (this.isGoodsOrder && data.goods_order_apply) {
          this.form.goods_order_apply = data.goods_order_apply
        }
      } catch (error) {
        console.error('获取退款详情失败:', error)
        this.$message.error('获取退款详情失败')
      } finally {
        this.loading = false
      }
    },

    // 退款方式切换
    onRefundTypeChange() {
      this.form.refund_amount = null
      this.form.refund_points = null
      this.$refs.formRef?.clearValidate()
    },

    // 校验退款金额
    validateAmount(rule, value, callback) {
      if (!this.isPartialRefund) return callback()
      if (!value && value !== 0) {
        return callback(new Error('请输入退款金额'))
      }
      const max = Number(this.maxRefundAmount)
      if (Number(value) > max) {
        return callback(new Error(`退款金额不能超过 ${max} 元`))
      }
      if (Number(value) <= 0) {
        return callback(new Error('退款金额必须大于 0'))
      }
      callback()
    },

    // 校验退款积分
    validatePoints(rule, value, callback) {
      if (!this.isPartialRefund || this.maxRefundPoints <= 0) return callback()
      if (!value && value !== 0) {
        return callback(new Error('请输入退款积分'))
      }
      const max = Number(this.maxRefundPoints)
      if (Number(value) > max) {
        return callback(new Error(`退款积分不能超过 ${max} 积分`))
      }
      if (Number(value) < 0) {
        return callback(new Error('退款积分不能为负数'))
      }
      callback()
    },

    // 实时校验
    validateRefundAmount() {
      this.$refs.formRef?.validateField('refund_amount')
    },
    validateRefundPoints() {
      this.$refs.formRef?.validateField('refund_points')
    },

    // 退款同意
    async handleRefundAgree() {
      try {
        await this.$refs.formRef?.validate()

        const isBuyOrder = [ORDER_TYPE.BUY, ORDER_TYPE.PAY].includes(this.orderType)

        // 构造请求数据
        const refundData = {
          id: this.form.id,
          refund_type: this.form.refund_type,
          price:''
        }
console.log(this.isPartialRefund,'999999')
        // 部分退款需要传金额和积分
        if (this.isPartialRefund) {
          refundData.price = this.form.refund_amount
          refundData.refund_points = Number(this.form.refund_points) || 0
        }

        // 输入备注
        const { value: remark } = await this.$prompt('请输入退款备注', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputType: 'textarea'
        })

        this.loading = true
        const api = isBuyOrder ? buy_refund : goods_refund

        if (isBuyOrder) {
          // 代买/代付退款接口
          await api({
            id: this.form.id,
            refund_type: this.form.refund_type,
            refund_remarks: remark,
            price:refundData.price
          })
        } else {
          // 商品退款接口
          await api({
            id: this.form.id,
            refund_remarks: remark
          })
        }

        this.$message.success('退款成功')
        this.$emit('change', true)
        this.handleClose()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('退款失败:', error)
        }
      } finally {
        this.loading = false
      }
    },

    // 退款驳回
    async handleRefundReject() {
      try {
        const { value: reason } = await this.$prompt('请输入驳回理由', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputType: 'textarea'
        })

        this.loading = true
        await refuse_refund({
          order_id: this.form.id,
          refund_remarks: reason
        })

        this.$message.success('已驳回')
        this.$emit('change', true)
        this.handleClose()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('驳回失败:', error)
        }
      } finally {
        this.loading = false
      }
    },

    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false
      this.$refs.formRef?.resetFields()
      // 重置表单数据
      this.form = {
        ...this.form,
        refund_amount: null,
        refund_points: null,
        refund_type: 0
      }
    }
  }
}
</script>

<style scoped>
.refund-goods-item {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.goods-img {
  width: 72px;
  height: 72px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
}

.goods-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  color: #333;
}

.goods-info span {
  line-height: 1.5;
}

.form-tip {
  display: block;
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

:deep(.el-dialog__body) {
  padding: 20px 30px;
}

:deep(.el-form-item) {
  margin-bottom: 18px;
}

:deep(.el-input.is-disabled .el-input__inner) {
  color: #333;
  background-color: #f5f7fa;
}
</style>